/**
 * useDirectoryHost — LOCKED PATTERN for directory host resolution
 * 
 * 🔒 ARCHITECTURAL INVARIANT: This pattern is LOCKED and cannot be modified
 * without explicit architect approval. Pre-commit hooks enforce this file
 * is read-only.
 * 
 * PURPOSE: Standardizes directory host resolution across all directory patterns
 * (elevator, cards, access, binders, etc.) using the same proven architecture
 * as resolveModuleTabs.
 * 
 * PATTERN: Nested function resolution (like DATEADD)
 * - Level 1: Read location.state.dirSeq (the directory being hosted)
 * - Level 2: Try manifest-based resolution (specific directory)
 * - Level 3: Fall back to component-based resolution (shared host default)
 * - Returns: directory metadata + buttons ready to render
 * 
 * ENFORCEMENT:
 * - ESLint blocks manual useDirectoryManifest calls in host components
 * - Pre-commit validator requires useDirectoryHost in all directory hosts
 * - Git hook blocks modifications to this file without architect approval
 * 
 * Created: 2026-07-23
 * Author: Architect (non-developer)
 * Status: LOCKED (validator enforced)
 */

import { useLocation } from 'react-router-dom';
import type { ManifestDirectory } from './manifest-types';
import { useDirectoryByComponent, useDirectoryManifest } from './useDirectoryManifest';

/**
 * Resolve directory host context with locked architectural pattern.
 * 
 * @param seq - Component sequence number (host component, e.g., 10009 for access gate)
 * @returns Directory metadata + buttons, or null if not found
 * 
 * @example
 * // In access gate host (component 10009)
 * const dir = useDirectoryHost(10009);
 * // When navigated with dirSeq=17 → loads access directory 17's manifest
 * // When navigated with dirSeq=18 → loads access directory 18's manifest
 * // Direct URL access → falls back to component 10009's default directory
 */
export function useDirectoryHost(seq: number): ManifestDirectory | null {
    const location = useLocation();
    
    // Level 1: Read the directory sequence from routing state
    // This is the SPECIFIC directory being hosted (e.g., access dir 17, floor dir 189)
    // Passed via button click: navigate(route, { state: { dirSeq: 17 } })
    const target_dir_seq = (location.state as { dirSeq?: number } | null)?.dirSeq;
    
    // Level 2: Try manifest-based resolution (specific directory)
    // If target_dir_seq is provided, load that directory's manifest
    const dirByManifest = useDirectoryManifest(target_dir_seq ?? -1);
    
    // Level 3: Fall back to component-based resolution (shared host default)
    // If no target_dir_seq, resolve by component sequence (picks isAppDefault)
    const dirByComponent = useDirectoryByComponent(seq);
    
    // Level 4: Choose resolution strategy
    // Prefer manifest-based (specific directory) over component-based (default)
    return (target_dir_seq != null && dirByManifest) ? dirByManifest : dirByComponent;
}

/**
 * 🔒 ARCHITECTURE CONTRACT:
 * 
 * All directory host components MUST use this hook instead of manual resolution.
 * This ensures consistent behavior across all directory patterns.
 * 
 * ✅ CORRECT PATTERN:
 * ```typescript
 * const dir = useDirectoryHost(10009);
 * const buttons = dir?.buttons ?? [];
 * ```
 * 
 * ❌ FORBIDDEN PATTERN (pre-commit blocks):
 * ```typescript
 * const dir = useDirectoryManifest(target_dir_seq ?? -1);
 * const dirByComponent = useDirectoryByComponent(seq);
 * const dir = target_dir_seq != null ? dirByManifest : dirByComponent;
 * ```
 * 
 * WHY THIS MATTERS:
 * - Prevents agent modifications that break nested function resolution
 * - Ensures all directory hosts follow identical pattern
 * - Single source of truth for directory resolution logic
 * - Enables atomic fixes (change once, fixes all hosts)
 * - Pre-commit enforcement prevents pattern drift
 */
